import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { formatInCST } from '../utils/timezone';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Wrench, Upload, CheckCircle, AlertCircle, Loader2, Link2,
  Users, FileText, Ticket, Monitor, Clock, ArrowRight,
  RefreshCw, Eye, X, Save, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export default function RepairShoprImport() {
  const [credentials, setCredentials] = useState({
    subdomain: '',
    api_key: ''
  });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedSettings, setSavedSettings] = useState(null);
  const [usingSaved, setUsingSaved] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    loadSavedSettings();
    loadHistory();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const loadSavedSettings = async () => {
    try {
      const response = await api.get('/import/settings/repairshopr');
      if (response.data.saved) {
        setSavedSettings(response.data);
        setCredentials({
          subdomain: response.data.credentials.subdomain || '',
          api_key: '',
        });
        setUsingSaved(true);
      }
    } catch (error) {
      console.error('Failed to load saved settings', error);
    }
  };

  const handleSaveSettings = async () => {
    if (!credentials.subdomain) {
      toast.error('Please fill in the subdomain before saving');
      return;
    }

    let credsToSave = { ...credentials };

    // If api_key is empty and we have saved settings, keep the saved key
    if (usingSaved && !credentials.api_key) {
      try {
        const fullCreds = await api.post('/import/settings/repairshopr/load');
        credsToSave.api_key = fullCreds.data.credentials.api_key || '';
      } catch (e) { /* ignore */ }
    }

    setSaving(true);
    try {
      await api.post('/import/settings/repairshopr', credsToSave);
      toast.success('Connection settings saved');
      await loadSavedSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSettings = async () => {
    try {
      await api.delete('/import/settings/repairshopr');
      setSavedSettings(null);
      setUsingSaved(false);
      setCredentials({ subdomain: '', api_key: '' });
      toast.success('Saved settings deleted');
    } catch (error) {
      toast.error('Failed to delete settings');
    }
  };

  const getActiveCredentials = async () => {
    if (usingSaved && !credentials.api_key) {
      try {
        const response = await api.post('/import/settings/repairshopr/load');
        return {
          ...credentials,
          api_key: response.data.credentials.api_key || '',
        };
      } catch (e) {
        toast.error('Failed to load saved credentials');
        return null;
      }
    }
    return credentials;
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get('/import/history?source=repairshopr&limit=20');
      setImportHistory(response.data);
    } catch (error) {
      console.error('Failed to load import history', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleTestConnection = async () => {
    const creds = await getActiveCredentials();
    if (!creds) return;
    if (!creds.subdomain || !creds.api_key) {
      toast.error('Please fill in all credential fields');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const response = await api.post('/import/repairshopr/test', creds);
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Successfully connected to RepairShopr');
      } else {
        toast.error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Connection test failed';
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const startImport = async (dataType) => {
    const creds = await getActiveCredentials();
    if (!creds) return;
    if (!creds.subdomain || !creds.api_key) {
      toast.error('Please enter RepairShopr credentials and test the connection first');
      return;
    }
    try {
      const response = await api.post(`/import/repairshopr/${dataType}`, creds);
      const jobId = response.data.job_id;
      setActiveJob({ id: jobId, status: 'pending', data_type: dataType, progress_current: 0, progress_total: 0, progress_message: 'Starting...' });
      toast.success(`${dataType} import started`);
      startPolling(jobId);
    } catch (error) {
      toast.error(`Failed to start ${dataType} import`);
    }
  };

  const startPolling = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const response = await api.get(`/import/jobs/${jobId}`);
        const job = response.data;
        setActiveJob(job);
        if (job.status === 'completed' || job.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          loadHistory();
          if (job.status === 'completed') {
            toast.success('Import completed successfully');
          } else {
            toast.error(`Import failed: ${job.error || 'Unknown error'}`);
          }
        }
      } catch (error) {
        console.error('Polling error', error);
      }
    }, 2000);
  }, []);

  const isRunning = activeJob && (activeJob.status === 'pending' || activeJob.status === 'running');

  const importCards = [
    { type: 'customers', label: 'Customers', icon: Users, desc: 'Customer accounts and contacts', color: 'text-blue-600' },
    { type: 'tickets', label: 'Tickets', icon: Ticket, desc: 'Support tickets and history', color: 'text-amber-600' },
    { type: 'assets', label: 'Assets', icon: Monitor, desc: 'Devices and equipment records', color: 'text-emerald-600' },
    { type: 'invoices', label: 'Invoices', icon: FileText, desc: 'Billing and invoice records', color: 'text-purple-600' },
  ];

  const getStatusBadge = (status) => {
    const map = {
      completed: <Badge data-testid="status-badge-completed" className="bg-emerald-100 text-emerald-700 border-emerald-200">Completed</Badge>,
      failed: <Badge data-testid="status-badge-failed" className="bg-red-100 text-red-700 border-red-200">Failed</Badge>,
      running: <Badge data-testid="status-badge-running" className="bg-blue-100 text-blue-700 border-blue-200">Running</Badge>,
      pending: <Badge data-testid="status-badge-pending" className="bg-neutral-100 text-neutral-700 border-neutral-200">Pending</Badge>,
    };
    return map[status] || <Badge variant="outline">{status}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return formatInCST(dateStr, 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const getResultSummary = (results) => {
    if (!results) return '-';
    const parts = [];
    for (const [key, val] of Object.entries(results)) {
      if (val && typeof val === 'object') {
        const created = val.created || 0;
        const updated = val.updated || 0;
        parts.push(`${key}: ${created} new, ${updated} updated`);
      }
    }
    return parts.length > 0 ? parts.join(' | ') : '-';
  };

  return (
    <div data-testid="repairshopr-import-page" className="space-y-6 p-6">
      {/* Page Header */}
      <div>
        <h1 data-testid="repairshopr-page-title" className="text-2xl font-bold text-neutral-900 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-50">
            <Wrench className="w-6 h-6 text-orange-600" />
          </div>
          RepairShopr Data Import
        </h1>
        <p className="text-sm text-neutral-500 mt-1 ml-12">
          Import customers, tickets, assets, and invoices from RepairShopr
        </p>
      </div>

      <Tabs defaultValue="import" className="w-full">
        <TabsList data-testid="repairshopr-tabs">
          <TabsTrigger value="import" data-testid="tab-import">Import</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        {/* ===== IMPORT TAB ===== */}
        <TabsContent value="import" className="space-y-6 mt-4">
          {/* Credentials Card */}
          <Card data-testid="repairshopr-credentials-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Link2 className="w-5 h-5 text-neutral-500" />
                Connection Settings
              </CardTitle>
              <CardDescription>Enter your RepairShopr subdomain and API key</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rs-subdomain">Subdomain</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-neutral-500 whitespace-nowrap">https://</span>
                    <Input
                      id="rs-subdomain"
                      data-testid="repairshopr-subdomain-input"
                      value={credentials.subdomain}
                      onChange={(e) => setCredentials({ ...credentials, subdomain: e.target.value })}
                      placeholder="your-company"
                      disabled={isRunning}
                    />
                    <span className="text-sm text-neutral-500 whitespace-nowrap">.repairshopr.com</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rs-api-key">API Key</Label>
                  <Input
                    id="rs-api-key"
                    data-testid="repairshopr-api-key-input"
                    type="password"
                    value={credentials.api_key}
                    onChange={(e) => { setCredentials({ ...credentials, api_key: e.target.value }); setUsingSaved(false); }}
                    placeholder={savedSettings?.has_values?.api_key ? "Using saved key (leave empty to keep)" : "Your RepairShopr API Key"}
                    disabled={isRunning}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  data-testid="repairshopr-test-connection-btn"
                  onClick={handleTestConnection}
                  variant="outline"
                  disabled={testing || isRunning}
                  className="min-w-[160px]"
                >
                  {testing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  {testing ? 'Testing...' : 'Test Connection'}
                </Button>

                <Button
                  data-testid="repairshopr-save-settings-btn"
                  onClick={handleSaveSettings}
                  variant="outline"
                  disabled={saving || isRunning}
                  className="min-w-[140px]"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>

                {savedSettings && (
                  <Button
                    data-testid="repairshopr-delete-settings-btn"
                    onClick={handleDeleteSettings}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    disabled={isRunning}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Clear Saved
                  </Button>
                )}

                {testResult && (
                  <div data-testid="repairshopr-test-result" className="flex items-center gap-2 text-sm">
                    {testResult.success ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        <span className="text-emerald-700">
                          Connected - {testResult.details?.total_customers} customers found
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-4 w-4 text-red-500" />
                        <span className="text-red-700">{testResult.message}</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {savedSettings && (
                <div data-testid="repairshopr-saved-indicator" className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                  <span>Settings saved{savedSettings.updated_at ? ` on ${formatInCST(savedSettings.updated_at, 'MMM d, yyyy h:mm a')}` : ''}</span>
                  <span className="text-neutral-300">|</span>
                  <span>API key is auto-loaded from saved settings when left empty</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Job Progress */}
          {activeJob && (
            <Card data-testid="repairshopr-active-job-card" className="border-orange-200 bg-orange-50/30">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {isRunning ? (
                      <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
                    ) : activeJob.status === 'completed' ? (
                      <CheckCircle className="h-5 w-5 text-emerald-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    )}
                    <span className="font-medium text-neutral-800">
                      {activeJob.data_type?.charAt(0).toUpperCase() + activeJob.data_type?.slice(1)} Import
                    </span>
                    {getStatusBadge(activeJob.status)}
                  </div>
                  {!isRunning && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="dismiss-job-btn"
                      onClick={() => setActiveJob(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <p className="text-sm text-neutral-600 mb-2">{activeJob.progress_message}</p>
                {activeJob.progress_total > 0 && (
                  <div className="space-y-1">
                    <Progress
                      data-testid="repairshopr-import-progress"
                      value={activeJob.progress_total > 0 ? (activeJob.progress_current / activeJob.progress_total) * 100 : 0}
                    />
                    <p className="text-xs text-neutral-500 text-right">
                      {activeJob.progress_current} / {activeJob.progress_total}
                    </p>
                  </div>
                )}
                {activeJob.status === 'completed' && activeJob.results && (
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(activeJob.results).map(([key, val]) => (
                      <div key={key} className="bg-white rounded-lg p-3 border border-neutral-200">
                        <p className="text-xs text-neutral-500 capitalize">{key}</p>
                        <p className="text-lg font-semibold text-neutral-800">{val?.created || 0} <span className="text-sm font-normal text-neutral-500">new</span></p>
                        {(val?.updated || 0) > 0 && (
                          <p className="text-xs text-neutral-500">{val.updated} updated</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {activeJob.error && (
                  <Alert className="mt-3 border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800 text-sm">{activeJob.error}</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Import Type Cards */}
          <div>
            <h2 className="text-base font-semibold text-neutral-800 mb-3">Import Data</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {importCards.map((card) => (
                <Card
                  key={card.type}
                  data-testid={`repairshopr-import-card-${card.type}`}
                  className="hover:shadow-md transition-shadow duration-200"
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2 rounded-lg bg-neutral-50 ${card.color}`}>
                        <card.icon className="w-5 h-5" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-neutral-800 mb-1">{card.label}</h3>
                    <p className="text-xs text-neutral-500 mb-4">{card.desc}</p>
                    <Button
                      data-testid={`repairshopr-import-btn-${card.type}`}
                      onClick={() => startImport(card.type)}
                      disabled={isRunning}
                      variant="outline"
                      className="w-full"
                      size="sm"
                    >
                      <Upload className="h-3.5 w-3.5 mr-1.5" />
                      Import {card.label}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-4">
              <Button
                data-testid="repairshopr-import-all-btn"
                onClick={() => startImport('all')}
                disabled={isRunning}
                className="bg-[#1a5490] hover:bg-[#0f3a5f] text-white"
              >
                {isRunning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {isRunning ? 'Import Running...' : 'Import All Data'}
              </Button>
            </div>
          </div>

          {/* How It Works */}
          <Alert className="bg-neutral-50 border-neutral-200">
            <AlertCircle className="h-4 w-4 text-neutral-500" />
            <AlertDescription className="text-neutral-700 text-sm">
              <strong>How it works:</strong> Data is fetched from RepairShopr via its REST API, mapped to CipherOne records,
              and inserted or updated (deduplicating by external ID or email match). Large imports run in the background
              and progress updates in real time.
            </AlertDescription>
          </Alert>
        </TabsContent>

        {/* ===== HISTORY TAB ===== */}
        <TabsContent value="history" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-neutral-800">Import History</h2>
            <Button
              data-testid="refresh-history-btn"
              variant="outline"
              size="sm"
              onClick={loadHistory}
              disabled={historyLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${historyLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {importHistory.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Clock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
                <p className="text-neutral-500 font-medium">No import history yet</p>
                <p className="text-sm text-neutral-400 mt-1">Import data from the Import tab to see history here</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table data-testid="repairshopr-history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Data Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Summary</TableHead>
                    <TableHead className="w-[80px]">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importHistory.map((entry) => (
                    <TableRow key={entry.id || entry.job_id}>
                      <TableCell className="text-sm text-neutral-600">{formatDate(entry.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{entry.data_type}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(entry.status)}</TableCell>
                      <TableCell className="text-sm text-neutral-600 max-w-[300px] truncate">
                        {entry.error || getResultSummary(entry.results)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`history-detail-btn-${entry.id}`}
                          onClick={() => setDetailDialog(entry)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="import-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Import Details
              {detailDialog && getStatusBadge(detailDialog.status)}
            </DialogTitle>
          </DialogHeader>
          {detailDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-neutral-500">Source</p>
                  <p className="font-medium text-neutral-800 capitalize">{detailDialog.source}</p>
                </div>
                <div>
                  <p className="text-neutral-500">Data Type</p>
                  <p className="font-medium text-neutral-800 capitalize">{detailDialog.data_type}</p>
                </div>
                <div>
                  <p className="text-neutral-500">Date</p>
                  <p className="font-medium text-neutral-800">{formatDate(detailDialog.created_at)}</p>
                </div>
                <div>
                  <p className="text-neutral-500">Job ID</p>
                  <p className="font-mono text-xs text-neutral-600">{detailDialog.job_id}</p>
                </div>
              </div>

              {detailDialog.error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 text-sm">{detailDialog.error}</AlertDescription>
                </Alert>
              )}

              {detailDialog.results && Object.keys(detailDialog.results).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-2">Results</p>
                  <div className="space-y-2">
                    {Object.entries(detailDialog.results).map(([key, val]) => (
                      <div key={key} className="bg-neutral-50 rounded-lg p-3 border border-neutral-200">
                        <p className="text-sm font-medium text-neutral-700 capitalize mb-1">{key}</p>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-neutral-500">Created: </span>
                            <span className="font-medium text-emerald-700">{val?.created || 0}</span>
                          </div>
                          <div>
                            <span className="text-neutral-500">Updated: </span>
                            <span className="font-medium text-blue-700">{val?.updated || 0}</span>
                          </div>
                          <div>
                            <span className="text-neutral-500">Skipped: </span>
                            <span className="font-medium text-neutral-600">{val?.skipped || 0}</span>
                          </div>
                        </div>
                        {val?.errors && val.errors.length > 0 && (
                          <div className="mt-2 text-xs text-red-600">
                            <p className="font-medium mb-1">{val.errors.length} errors:</p>
                            {val.errors.slice(0, 5).map((err, i) => (
                              <p key={i} className="truncate">{err}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
